/**
 * Copyright © 2025-2026 Delicious AI, LLC
 */

import { green, grey, indigo, red } from '@mui/material/colors'
import merge from 'deepmerge'

import { Colors } from '@/components/Colors'
import { THEMES } from '@/constants'

const customBlue = {
  50: '#e9f0fb',
  100: '#c8daf4',
  200: '#a3c1ed',
  300: '#7ea8e5',
  400: '#6395e0',
  500: '#4782da',
  600: '#407ad6',
  700: '#376fd0',
  800: '#2f65cb',
  900: '#2052c2',
}

const defaultVariant = {
  name: THEMES.DEFAULT,
  palette: {
    mode: 'light',
    primary: {
      main: Colors.PRIMARY,
      contrastText: Colors.WHITE,
    },
    secondary: {
      main: Colors.SECONDARY,
      contrastText: Colors.WHITE,
    },
    background: {
      default: '#F7F9FC',
      paper: Colors.WHITE,
      card: grey[200],
    },
    task: {
      completion: {
        background: '#f8fff8',
        border: '#4caf50',
      },
      note: {
        background: '#f8fbff',
        border: '#2196f3',
      },
    },
  },
  header: {
    color: grey[500],
    background: Colors.WHITE,
    search: {
      color: grey[800],
    },
    indicator: {
      background: Colors.PRIMARY_600,
    },
  },
  footer: {
    color: Colors.BLACK,
    background: Colors.WHITE,
  },
  sidebar: {
    color: grey[800],
    background: Colors.WHITE,
    header: {
      color: grey[800],
      background: Colors.WHITE,
      brand: {
        color: Colors.BLACK,
      },
    },
    footer: {
      color: grey[700],
      background: Colors.WHITE,
    },
    badge: {
      color: Colors.WHITE,
      background: Colors.BLUE_5,
    },
  },
}

const darkVariant = merge(defaultVariant, {
  name: THEMES.DARK,
  palette: {
    mode: 'dark',
    primary: {
      main: red[600],
      contrastText: Colors.WHITE,
    },
    secondary: {
      main: grey[600],
      contrastText: Colors.WHITE,
    },
    background: {
      default: '#212121',
      paper: Colors.DARK_GRAY,
      card: grey[800],
    },
    text: {
      primary: grey[50],
      secondary: grey[400],
    },
    task: {
      completion: {
        background: '#1a2e1a',
        border: '#4caf50',
      },
      note: {
        background: '#1a1e2e',
        border: '#2196f3',
      },
    },
  },
  sidebar: {
    color: grey[300],
    background: Colors.DARK_GRAY,
    header: {
      color: grey[50],
      background: Colors.DARK_GRAY,
      brand: {
        color: grey[50],
      },
    },
    footer: {
      color: grey[300],
      background: Colors.DARK_GRAY,
    },
  },
  header: {
    color: grey[200],
    background: '#212121',
    search: {
      color: grey[300],
    },
  },
  footer: {
    color: grey[400],
    background: Colors.DARK_GRAY,
  },
})

const lightVariant = merge(defaultVariant, {
  name: THEMES.LIGHT,
  palette: {
    mode: 'light',
  },
  header: {
    color: grey[200],
    background: '#F7F9FC',
    search: {
      color: grey[100],
    },
    indicator: {
      background: red[700],
    },
  },
  sidebar: {
    color: grey[900],
    background: Colors.WHITE,
    header: {
      color: Colors.WHITE,
      background: Colors.WHITE,
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: grey[800],
      background: Colors.WHITE,
    },
  },
})

const blueVariant = merge(defaultVariant, {
  name: THEMES.BLUE,
  palette: {
    mode: 'light',
  },
  sidebar: {
    color: Colors.WHITE,
    background: customBlue[700],
    header: {
      color: Colors.WHITE,
      background: customBlue[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: Colors.WHITE,
      background: customBlue[800],
    },
    badge: {
      color: '#000',
      background: Colors.WHITE,
    },
  },
})

const greenVariant = merge(defaultVariant, {
  name: THEMES.GREEN,
  palette: {
    primary: {
      main: green[800],
      contrastText: Colors.WHITE,
    },
    secondary: {
      main: green[500],
      contrastText: Colors.WHITE,
    },
  },
  header: {
    indicator: {
      background: green[600],
    },
  },
  sidebar: {
    color: Colors.WHITE,
    background: green[700],
    header: {
      color: Colors.WHITE,
      background: green[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: Colors.WHITE,
      background: green[800],
    },
    badge: {
      color: '#000',
      background: Colors.WHITE,
    },
  },
})

const indigoVariant = merge(defaultVariant, {
  name: THEMES.INDIGO,
  palette: {
    primary: {
      main: indigo[600],
      contrastText: Colors.WHITE,
    },
    secondary: {
      main: indigo[400],
      contrastText: Colors.WHITE,
    },
  },
  header: {
    indicator: {
      background: indigo[600],
    },
  },
  sidebar: {
    color: Colors.WHITE,
    background: indigo[700],
    header: {
      color: Colors.WHITE,
      background: indigo[800],
      brand: {
        color: '#FFFFFF',
      },
    },
    footer: {
      color: Colors.WHITE,
      background: indigo[800],
    },
    badge: {
      color: '#000',
      background: Colors.WHITE,
    },
  },
})

const variants: Array<VariantType> = [
  defaultVariant,
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant,
]

export default variants

export type VariantType = {
  name: string
  palette: {
    primary: MainContrastTextType
    secondary: MainContrastTextType
    task: {
      completion: {
        background: string
        border: string
      }
      note: {
        background: string
        border: string
      }
    }
  }
  header: ColorBgType & {
    search: {
      color: string
    }
    indicator: {
      background: string
    }
  }
  footer: ColorBgType
  sidebar: ColorBgType & {
    header: ColorBgType & {
      brand: {
        color: string
      }
    }
    footer: ColorBgType
    badge: ColorBgType
  }
}

type MainContrastTextType = {
  main: string
  contrastText: string
}
type ColorBgType = {
  color: string
  background: string
}
